<script setup lang="ts">
import SectionTitle from './SectionTitle.vue';
import AnchorLinkFlat from 'theme/components/atoms/AnchorLinkFlat.vue';
import { captureException } from '~/utils/sentry-workaround';

const props = defineProps<{
  name: string;
  myStoryUrl: string;
}>();

const sectionTitle = computed(() => `${props.name}のMy story`);

type MyStory = {
  ogImageUrl: string;
  ogTitle: string;
};
const myStoryRef = ref<MyStory | null>(null);

const config = useRuntimeConfig();
const url = computed(() =>
  // CORS 制約により、環境をまたいでデータの取得ができない（例：alpha から *.okane-kenko.jp）
  // よって、productionでない場合は、適当にshadowのトップページOGP画像を返す
  config.public.serverEnv === 'production' ? props.myStoryUrl : '/'
);

onMounted(async () => {
  try {
    const response = await fetch(url.value);
    if (!response.ok || response.body === null) {
      captureException(new Error('Failed to fetch my story page'));
      return;
    }

    const buffer = await streamToArrayBuffer(response.body);

    const decoder = new TextDecoder('utf-8');
    const document = decoder.decode(buffer);
    const domparser = new DOMParser();
    const dom = domparser.parseFromString(document, 'text/html');

    const ogImageUrl =
      dom
        .querySelector('meta[property="og:image"]')
        ?.attributes.getNamedItem('content')?.value ?? null;
    const myStoryTitle =
      dom
        .querySelector('meta[property="og:title"]')
        ?.attributes.getNamedItem('content')?.value ?? null;

    // オカネコマガジンから取得するogtitleは`〇〇 | オカネコマガジン`になっている
    // プランナー詳細ページに表示されるときは` | オカネコマガジン`の部分はいらないので、replaceしている
    const title = myStoryTitle?.replace(/\s\|\sオカネコマガジン$/, '') ?? null;

    if (ogImageUrl === null || title === null) {
      captureException(
        new Error(`Failed to fetch my story page. url: ${url.value}`)
      );
      return;
    }

    myStoryRef.value = {
      ogImageUrl,
      ogTitle: title,
    };
  } catch (e) {
    captureException(e);
  }
});

async function streamToArrayBuffer(
  stream: ReadableStream<Uint8Array>
): Promise<Uint8Array> {
  let result = new Uint8Array(0);
  const reader = stream.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    const newResult = new Uint8Array(result.length + value.length);
    newResult.set(result);
    newResult.set(value, result.length);
    result = newResult;
  }
  return result;
}
</script>

<template>
  <section v-if="myStoryRef !== null" :class="$style.section">
    <SectionTitle
      :title="sectionTitle"
      :class="[$style.section__title, $style.title]"
    />
    <div :class="[$style.section__card, $style.card]">
      <img
        :src="myStoryRef.ogImageUrl"
        alt=""
        :class="[$style.card__image, $style.image]"
      />
      <div :class="$style['text-section']">
        <p :class="[$style['mystory-title'], $style.card__title]">
          {{ myStoryRef.ogTitle }}
        </p>
        <AnchorLinkFlat :href="myStoryUrl" target="_blank" variant="plain"
          >{{ name }}のMy Storyを見る</AnchorLinkFlat
        >
      </div>
    </div>
  </section>
</template>

<style lang="scss" module>
.section {
  &__card {
    margin-top: 12px;
  }
}

.card {
  outline: 1px solid $color-border-low-emphasis;
  outline-offset: -1px;
  border-radius: 8px;

  &__button {
    width: 100%;
  }
}

.image {
  border-radius: 8px 8px 0 0;
}

.text-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
}

.mystory-title {
  font-size: 16px;
  font-weight: bold;
}
</style>
